import {
  SanityPrintFeeCombination,
  SanityPrintFeeCombinationItem,
} from '@data/sanity/queries/types/print'
import {
  SanityPrintColor,
  SanityShopPrint,
} from '@data/sanity/queries/types/shop'
import { AttributeInput } from '@data/shopify/storefront/types'
import { CartLineItem, LineItem, LineItemAttribute } from '@lib/cart'
import { filterDuplicates } from '@lib/helpers'
import { getSanityImageUrl } from '@lib/image'
import { Locale } from '@lib/language'
import {
  assetFilenameAttributeKey,
  imageAssetColorAttributeKey,
  imageAssetFileIdAttributeKey,
  imageAssetTitleAttributeKey,
  imageAssetUrlAttributeKey,
  institutionIdAttributeKey,
  logoAssetColorAttributeKey,
  logoAssetFileIdAttributeKey,
  logoAssetTitleAttributeKey,
  logoAssetUrlAttributeKey,
  textAttributeKey,
  textColorAttributeKey,
} from '@lib/shopify/cart'
import { uploadPrintImageToShopify } from './configurator'
import { getActivePrintFeeCombinations } from './fee'
import { SlotWithConfiguration } from './types'

/**
 * Gets all institution IDs from cart line items.
 */
export const getCartItemInstitutionIds = (lineItems: LineItem[]) => {
  const institutionIds = lineItems
    .map((lineItem) => {
      const attribute = lineItem.attributes?.find(
        (attribute) => attribute.key === `_${institutionIdAttributeKey}`,
      )
      return attribute?.value
    })
    .filter(Boolean) as string[]

  return institutionIds.filter((institutionId, index, array) =>
    filterDuplicates(institutionId, index, array),
  )
}

/**
 * Gets logo line item attributes.
 */
const getLogoAttributes = (shopPrint: SanityShopPrint, fileId?: string) => {
  const logoAssetUrl = getSanityImageUrl(shopPrint.printImage)
  const attributes: LineItemAttribute[] = [
    {
      key: `_${logoAssetUrlAttributeKey}`,
      value: logoAssetUrl ?? '',
    },
  ]

  if (fileId) {
    attributes.push({
      key: `_${logoAssetFileIdAttributeKey}`,
      value: fileId,
    })
  }

  if (shopPrint.title) {
    attributes.push({
      key: `_${logoAssetTitleAttributeKey}`,
      value: shopPrint.title,
    })
  }

  if (shopPrint.printColor) {
    attributes.push({
      key: `_${logoAssetColorAttributeKey}`,
      value: shopPrint.printColor,
    })
  }

  if (shopPrint.printImage?.filename) {
    attributes.push({
      key: `_${assetFilenameAttributeKey}`,
      value: shopPrint.printImage.filename,
    })
  }

  return attributes
}

/**
 * Gets image print line item attributes.
 */
const getImageAttributes = (shopPrint: SanityShopPrint, fileId?: string) => {
  const imageAssetUrl = getSanityImageUrl(shopPrint.printImage)
  const attributes: LineItemAttribute[] = [
    {
      key: `_${imageAssetUrlAttributeKey}`,
      value: imageAssetUrl ?? '',
    },
  ]

  if (fileId) {
    attributes.push({
      key: `_${imageAssetFileIdAttributeKey}`,
      value: fileId,
    })
  }

  if (shopPrint.title) {
    attributes.push({
      key: `_${imageAssetTitleAttributeKey}`,
      value: shopPrint.title,
    })
  }

  if (shopPrint.printColor) {
    attributes.push({
      key: `_${imageAssetColorAttributeKey}`,
      value: shopPrint.printColor,
    })
  }

  if (shopPrint.printImage?.filename) {
    attributes.push({
      key: `_${assetFilenameAttributeKey}`,
      value: shopPrint.printImage.filename,
    })
  }

  return attributes
}

/**
 * Gets text fee line item attributes.
 */
const getTextAttributes = (text: string, textColor: SanityPrintColor) => {
  const attributes: LineItemAttribute[] = [
    {
      key: `_${textAttributeKey}`,
      value: text,
    },
    {
      key: `_${textColorAttributeKey}`,
      value: textColor,
    },
  ]

  return attributes
}

/**
 * Gets print fee line item attributes.
 */
const getPrintFeeAttributes = async (
  locale: Locale,
  item: SanityPrintFeeCombinationItem,
  slots: SlotWithConfiguration[],
) => {
  let logoAttributes: AttributeInput[] = []
  let imageAttributes: AttributeInput[] = []
  let textAttributes: AttributeInput[] = []

  const slot = slots.find((slot) => slot._id === item.slot._id)
  const configuration = slot?.configuration

  if (
    item.type === 'logo' &&
    configuration?.logoEnabled === 'yes' &&
    configuration?.logo
  ) {
    const fileId = await uploadPrintImageToShopify(
      locale,
      configuration.logo.printImage,
    )
    logoAttributes = getLogoAttributes(configuration.logo, fileId)
  }

  if (
    item.type === 'image' &&
    configuration?.imageEnabled === 'yes' &&
    configuration?.image
  ) {
    const fileId = await uploadPrintImageToShopify(
      locale,
      configuration.image.printImage,
    )
    imageAttributes = getImageAttributes(configuration.image, fileId)
  }

  if (
    item.type === 'text' &&
    configuration?.freetextEnabled === 'yes' &&
    configuration?.freetext
  ) {
    textAttributes = getTextAttributes(
      configuration.freetext,
      configuration.freetextColor,
    )
  }

  return [...logoAttributes, ...imageAttributes, ...textAttributes]
}

/**
 * Get fee product cart line items.
 */
export const getPrintFeeLineItems = async (
  locale: Locale,
  printFeeCombinations: SanityPrintFeeCombination[],
  slots: SlotWithConfiguration[],
  extraAttributes: AttributeInput[] = [],
) => {
  const items: CartLineItem[] = []
  const quantity = 1

  const activePrintFeeCombinations = getActivePrintFeeCombinations(
    printFeeCombinations,
    slots,
  )

  if (!activePrintFeeCombinations) {
    return items
  }

  // Add a line item for each print fee combination
  for (const printFeeCombination of activePrintFeeCombinations) {
    for (const item of printFeeCombination.printFeeCombinationItems) {
      const printFeeAttributes = await getPrintFeeAttributes(
        locale,
        item,
        slots,
      )

      items.push({
        id: item.productVariant.id,
        quantity,
        attributes: [...printFeeAttributes, ...extraAttributes],
      })
    }
  }

  return items
}
